import * as _ from 'lodash';
import * as api from '../../../api/api.service';

import * as featuresApi from '../../../api/features.service';
import * as offersApi from '../../../api/offers.service';

class SavingsPuckCtrl {
  static bindings = {
    account: '<',
    featureList: '<',
    puck: '<',
    rowNumber: '<',
    columnNumber: '<',
    onOpenPuck: '&',
    offerId: '<',
    tierId: '<',
    usePrimaryEquivalentCrosses: '<'
  };

  account;
  featureList: featuresApi.FeatureFlags;
  puck;
  onOpenPuck;
  rowNumber;
  columnNumber;
  offerId;
  tierId;
  usePrimaryEquivalentCrosses;

  microChartData;

  // isSummaryLoaded = false;
  areDetailsLoaded = false;

  constructor(
    private NavMessages,
    private $filter,
    private OfferImpactService,
    private api: api.IApiService
  ) {
    'ngInject';
  }

  $onInit() {
    this.loadPuckSummary();
  }

  $onChanges(changes) {
    // Reload when this value changes (except for first change)
    if (
      changes.usePrimaryEquivalentCrosses &&
      !changes.usePrimaryEquivalentCrosses.isFirstChange()
    ) {
      // console.log('usePrimaryEquivalentCrosses changed, reloading puck summaries');
      this.loadPuckSummary();
    }

    if (changes.tierId) {
      this.calculatePuckSummary();
    }

    if (changes.offerId && !changes.offerId.isFirstChange()) {
      this.reloadPuckData();
    }
  }

  reloadPuckData() {
    this.api.offers
      .getMicrochart(
        this.offerId,
        this.puck.id,
        this.usePrimaryEquivalentCrosses,
        { cache: true }
      )
      .then((microChartData) => {
        this.microChartData = microChartData;
        this.calculatePuckSummary(microChartData);
      });
  }

  loadPuckSummary() {
    // this.isSummaryLoaded = true;

    if (this.offerId == null || this.puck.id == null) {
      return false;
    }

    if (
      this.account.isBuyer ||
      (this.account.isSeller && this.usePrimaryEquivalentCrosses != null)
    ) {
      this.api.offers
        .getMicrochart(
          this.offerId,
          this.puck.id,
          this.usePrimaryEquivalentCrosses,
          { cache: true }
        )
        .then((microChartData) => {
          this.microChartData = microChartData;
          this.calculatePuckSummary(microChartData);
        });
    }
  }

  /**
   * If tiers change, we have enough data to update the summary without reloading any data
   *
   * When we have new values to put into the puck (for example, savings), this function handles copying it so that
   * code isn't repeated multiple times.
   *
   * @param microChartData This is optional, depending whether we're loading new data or using data already loaded
   * @private
   */
  private calculatePuckSummary(microChartData?: offersApi.MicroChart) {
    let puck = this.puck;
    // this may no longer be relevant
    const allocatedMarketShare = _.get(
      puck,
      'activeSellerImpact.complianceSpendLevel',
      {}
    ).dollarMarketShare;

    if (microChartData) {
      // console.log('Loading microchart data...');
      puck.microChartData = microChartData;
    } else {
      // console.log('Updating microchart data...');
    }

    if (puck.microChartData == null) {
      return false;
    }

    // Buyer side passes this info in
    if (this.account.isSeller) {
      let tierData = _.find(puck.microChartData.tiers, {
        tier: {
          id: this.tierId
        }
      });

      puck.continuedSpend = puck.microChartData.last12MonthsSpend;
      puck.plannedSpend = _.get(tierData, 'minSpend'); // || 0;
      puck.savings = -_.get(tierData, 'min') || 0;
      puck.potentialMarketSharePercentage =
        _.get(tierData, 'minAllocationPct') || 0;
      puck.conversionsNeededCount =
        _.get(tierData, 'minNumberOfConversions') || 0;
      // this may no longer be relevant
      puck.allocatedMarketShare = allocatedMarketShare;
    }
  }

  openPuck() {
    this.onOpenPuck({
      $event: {
        puck: this.puck,
        rowNumber: this.rowNumber,
        columnNumber: this.columnNumber,
        microChartData: this.microChartData
      }
    });
  }

  /**
   * Seller side uses puck.status, buyer side uses puck.activeSellerImpact.inclusionStatus
   * TODO: See if we can make them the same outside this component
   */
  puckClass(puck): any {
    let status =
      puck.status || _.get(puck, 'activeSellerImpact.inclusionStatus');

    switch (status) {
      case 'In':
        return { inColor: true };
      case 'Out':
        return { outColor: true };
      case 'Undecided':
        return { undecidedColor: true };
      default:
        return { outColor: true };
    }
  }

  getSavingsPercentage() {
    // TODO: Some of these values aren't calculating the same as in qa
    // console.log(this.puck.name);
    // console.log(this.puck.plannedSpend);
    // console.log(this.puck.continuedSpend);

    return this.puck.plannedSpend
      ? Math.round((this.puck.savings / this.puck.continuedSpend) * 100.0)
      : 0;
    // targetPuck.potentialMarketSharePercentage =
    //     puckDetails.potentialSpend ? Math.round((puckDetails.plannedSpend / puckDetails.potentialSpend) * 100.0) : 0;
  }

  // Depending upon the information available for the puck, display either nothing, a number, or a message to the user.
  getPuckSavings() {
    if (this.puck.savings != null) {
      if (this.account.isSeller && this.OfferImpactService.noSpend(this.puck)) {
        return 'Spend Not Included';
      } else {
        return this.$filter('nfcurrency')(this.puck.savings);
      }
    } else {
      return ' ';
    }
  }

  getPuckSavingsClass() {
    if (this.puck.savings < 0) {
      return 'negative';
    } else if (this.puck.savings > 0) {
      return 'positive';
    }
  }

  getPuckTierTypeLabel() {
    return _.get(
      this.puck,
      'activeSellerImpact.tier.spendLevelType.displayName'
    );
  }
}

export const SavingsPuckComponent = {
  template: require('./savingsPuck.component.html'),
  controller: SavingsPuckCtrl,
  bindings: SavingsPuckCtrl.bindings
};
